import React from "react";
import { t } from "@auto/services";

import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import {
  isCorrectionFilter,
  reportingResourceKey,
} from "@src/reporting/model/reportingFilters.mjs";
import { count, dateTime, labelOf, percent } from "@src/reporting/model/reportingFormatters.mjs";
import { loadReportingVotingCenters } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const PAGE_SIZE = 50;

const voterUtilization = (row) => {
  const voters = Number(row?.registeredVoterCount);
  const capacity = Number(row?.capacity);

  if (!Number.isFinite(voters) || !Number.isFinite(capacity) || capacity <= 0) {
    return null;
  }

  return voters * 100 / capacity;
};

const VotingCenterCapacityWidget = ({ gui, filter, refreshToken = 0 }) => {
  const correction = isCorrectionFilter(filter);
  const [page, setPage] = React.useState(1);
  const filterKey = reportingResourceKey("voting-center-filter", filter);

  React.useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const resource = useReportingResource(
    () => loadReportingVotingCenters(filter, {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    }),
    {
      key: reportingResourceKey("voting-centers", filter, page),
      refreshToken,
      initialData: { total: 0, offset: 0, limit: PAGE_SIZE, items: [] },
    },
  );

  const data = resource.data || { total: 0, items: [] };
  const rows = data.items || [];
  const totalPages = Math.max(1, Math.ceil(Number(data.total || 0) / PAGE_SIZE));

  return (
    <ReportingWidget
      title={correction ? t("reporting.correctionsByCenter") : t("reporting.centerRegistrationCapacity")}
      subtitle={correction
        ? t("reporting.correctionsCenterSubtitle")
        : t("reporting.registrationCenterSubtitle")}
      actions={<span className={styles.resultCount}>{count(data.total)} {t("reporting.centersLower")}</span>}
      loading={resource.loading}
      error={resource.error}
      empty={resource.hasLoaded && rows.length === 0}
    >
      <div className={styles.tableWrap}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("voter.votingCenter")}</TableCell>
                <TableCell>{t("common.geography")}</TableCell>
                <TableCell align="right">{t("reporting.registeredVoters")}</TableCell>
                <TableCell align="right">{t("reporting.operators")}</TableCell>
                {!correction ? <TableCell align="right">{t("geo.capacity")}</TableCell> : null}
                {!correction ? <TableCell align="right">{t("reporting.utilization")}</TableCell> : null}
                <TableCell>{t("reporting.lastActivity")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.gid}
                  hover
                >
                  <TableCell>
                    <div className={styles.primaryCell}>{labelOf(row)}</div>
                  </TableCell>
                  <TableCell>
                    <div className={styles.primaryCell}>{row.communeName || "—"}</div>
                    <div className={styles.secondaryCell}>
                      {[row.departmentName, row.arrondissementName, row.sectionName].filter(Boolean).join(" / ")}
                    </div>
                  </TableCell>
                  <TableCell align="right">{count(row.registeredVoterCount)}</TableCell>
                  <TableCell align="right">{count(row.activeOperatorCount)}</TableCell>
                  {!correction ? (
                    <TableCell align="right">
                      {row.capacity === null || row.capacity === undefined ? "—" : count(row.capacity)}
                    </TableCell>
                  ) : null}
                  {!correction ? <TableCell align="right">{percent(voterUtilization(row))}</TableCell> : null}
                  <TableCell>{dateTime(row.lastSubmissionAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {totalPages > 1 ? (
        <div className={styles.pagination}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, nextPage) => setPage(nextPage)}
            disabled={resource.loading}
          />
        </div>
      ) : null}
    </ReportingWidget>
  );
};

export default VotingCenterCapacityWidget;
